// apps/admin/src/features/billing/TopupProofReviewModal.tsx
//
// Phase 8.2 Wave 2 Task 06 — Topup Proof Review modal (manual_review branch).
//
// Per CONTEXT.md
//   • Billing tab structure (Topup Reviews lives under Billing).
//   • Topup review branches on `verification_status`
//                  (`auto_verified` → inline 1-click | `manual_review` → modal).
//   • Manual review modal exposes 4 sections (IMAP raw + parsed
//                  merchant memo, screenshot/bill upload, transaction
//                  history + wallet balance + suspicious flags).
//   • Approve path writes audit log on backend.
//   • Reject reason is a 7-enum dropdown
//                  (`wrong_amount` | `wrong_memo` | `unknown_sender`
//                  | `duplicate` | `suspicious` | `missing_proof` | `other`)
//                  with required free-text when `other` is chosen.
//   • SLA badge: pending >2h yellow, >4h red.
//
// Per INTERFACE-STANDARDS.md
//   • Error toast priority `error.user_message > error.message > fallback`
//     resolved by `getUserMessage()` from `lib/errorHandler.ts`.
//   • Success / error envelope unwrap delegated to the shared `apiClient`
//     wrapper — this modal never reads `AxiosError.message` directly.
//
// Audit log is written backend-side on both approve and reject — the
// modal only surfaces a success toast referencing the audit_log_id
// returned in the response payload.
//
// vg-binding: PLAN:task-06
// vg-binding: P8.D-84
// vg-binding: P8.D-214
// vg-binding: P8.D-447
// vg-binding: P8.D-448
// vg-binding: P8.D-449
// vg-binding: P8.D-450
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority

import { useEffect, useState, type JSX } from 'react';

import { resolveApiBaseUrl } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@printway/ui';

import { Money } from '../../components/ui/Money.js';
import type { TopupReviewRow } from '../../types/finance.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Reject reason enum — wire values mirror API-CONTRACTS §POST
// /api/v1/admin/topups/:id/reject Block 2 (`TopupRejectReason`). Display
// labels stay in English for the admin shell (Vietnamese-first product
// surface is documented at the toast/banner layer, not enum codes).
// ---------------------------------------------------------------------------

export const TOPUP_REJECT_REASONS: ReadonlyArray<{
  value: TopupRejectReason;
  labelKey: string;
}> = [
  { value: 'wrong_amount', labelKey: 'topup_review.modal.rejectReason.wrong_amount' },
  { value: 'wrong_memo', labelKey: 'topup_review.modal.rejectReason.wrong_memo' },
  { value: 'unknown_sender', labelKey: 'topup_review.modal.rejectReason.unknown_sender' },
  { value: 'duplicate', labelKey: 'topup_review.modal.rejectReason.duplicate' },
  { value: 'suspicious', labelKey: 'topup_review.modal.rejectReason.suspicious' },
  { value: 'missing_proof', labelKey: 'topup_review.modal.rejectReason.missing_proof' },
  { value: 'other', labelKey: 'topup_review.modal.rejectReason.other' },
] as const;

export type TopupRejectReason =
  | 'wrong_amount'
  | 'wrong_memo'
  | 'unknown_sender'
  | 'duplicate'
  | 'suspicious'
  | 'missing_proof'
  | 'other';

// ---------------------------------------------------------------------------
// Proof URL absolutiser — issue #2815 (debug 2026-07-27)
//
// The BE serialises `proof_url` as the ROOT-RELATIVE path
// `/api/v1/files/<receipt_file_id>` (topup.admin.ts §rowToReviewRow). Axios
// calls survive that because `apiClient` prepends `VITE_API_BASE_URL`, but an
// `<img src>` / `<a href>` is resolved by the browser against the CURRENT
// page origin — `https://admin-v3.printway.work` — where nginx is
// static-only (no `/api` proxy_pass). The SPA history fallback answers with
// `index.html` (200 text/html), so the proof image rendered blank even though
// the bytes exist and admin is authorised.
//
// Prefixing `resolveApiBaseUrl()` sends the request to `api-v3` instead. The
// serve route already replies `Cross-Origin-Resource-Policy: cross-origin`
// and the auth cookie is `Domain=.printway.work`, so the cross-subdomain
// `<img>` load succeeds. Mirrors the same fix already applied to the receipt
// links + CSV export in `TopupQueuePage.tsx` (F-11, review 3.2).
//
// Idempotent: absolute (`http(s)://`), protocol-relative (`//`) and `data:`
// URLs pass through untouched so a future BE change to absolute URLs — or the
// already-absolute `proof.screenshot_url` — cannot end up double-prefixed.
// ---------------------------------------------------------------------------

export function absolutizeProofUrl(url: string | null | undefined): string | null {
  const raw = (url ?? '').trim();
  if (!raw) return null;
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  const base = resolveApiBaseUrl().replace(/\/$/, '');
  return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

// ---------------------------------------------------------------------------
// Optional companion-context shape — the BE may include a richer payload
// (IMAP-parsed amount/sender, wallet balance, recent txn rows, flags)
// alongside the row. We model it as an opt-in side payload so the modal
// can render whichever sections the BE populates without crashing if a
// section is missing.
// ---------------------------------------------------------------------------

export interface TopupReviewProofContext {
  /** Raw IMAP message (truncated to 8KB by BE; rendered verbatim). */
  imap_raw?: string | null;
  /** Server-parsed IMAP fields (best-effort regex extraction). */
  imap_parsed?: {
    sender?: string | null;
    amount?: string | null;
    memo?: string | null;
    timestamp?: string | null;
  } | null;
  /** Optional screenshot/bill upload URL (≤5MB enforced by BE). */
  screenshot_url?: string | null;
  /** Snapshot of the merchant wallet at the time of request creation. */
  wallet_balance_cents?: number | null;
  /** Recent merchant transactions (last 5, descending by date). */
  recent_transactions?: Array<{
    id: string;
    type: string;
    amount_cents: number;
    created_at: string;
  }>;
  /** Backend-attached suspicious flags (mass-deposit, repeat, etc.). */
  suspicious_flags?: string[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TopupProofReviewModalProps {
  /** Controls visibility — close via `onOpenChange(false)`. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row under review (drives header + amount + memo + proof). */
  row: TopupReviewRow;
  /** Optional enriched proof context (IMAP raw, txn history, flags). */
  proof?: TopupReviewProofContext | null;
  /** Approve handler — called with optional notes. */
  onApprove: (input: { notes?: string }) => Promise<void> | void;
  /** Reject handler — receives reason enum + free-text when `other`. */
  onReject: (input: {
    reason: TopupRejectReason;
    reason_text?: string;
  }) => Promise<void> | void;
  /** Disable buttons + show inline state while mutation is in flight. */
  submitting?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<TopupProofReviewModal>` — manual-review branch of the topup queue.
 * Renders 4 sections (IMAP raw + parsed, merchant memo, screenshot
 * preview, transaction history + wallet balance + suspicious flags) and
 * exposes an approve/reject footer. Reject enforces the 7-enum
 * dropdown with required free-text when `other` is selected.
 */
export function TopupProofReviewModal(
  props: TopupProofReviewModalProps,
): JSX.Element {
  const {
    open,
    onOpenChange,
    row,
    proof,
    onApprove,
    onReject,
    submitting = false,
  } = props;
  const { t } = useTranslation('finance');

  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState<TopupRejectReason | ''>('');
  const [rejectText, setRejectText] = useState('');
  const [rejectFormError, setRejectFormError] = useState<string | null>(null);
  // Tracks a failed proof-image fetch so we can degrade to a plain link
  // instead of a broken-image icon (issue #2815).
  const [proofLoadFailed, setProofLoadFailed] = useState(false);

  // Reset form state every time the modal opens for a different row so
  // stale rejection text from a prior review can never bleed through.
  useEffect(() => {
    if (open) {
      setNotes('');
      setRejectReason('');
      setRejectText('');
      setRejectFormError(null);
      setProofLoadFailed(false);
    }
  }, [open, row.id]);

  const slaClass = slaBadgeClass(row.sla_badge);

  // Issue #2815 — resolve against the API origin, never the SPA origin.
  const proofUrl = absolutizeProofUrl(row.proof_url ?? proof?.screenshot_url);

  async function handleApprove(): Promise<void> {
    await onApprove({ notes: notes.trim() ? notes.trim() : undefined });
  }

  async function handleReject(): Promise<void> {
    if (!rejectReason) {
      setRejectFormError(t('topup_review.modal.selectReasonError'));
      return;
    }
    if (rejectReason === 'other' && rejectText.trim().length === 0) {
      setRejectFormError(t('topup_review.modal.otherRequiredError'));
      return;
    }
    setRejectFormError(null);
    await onReject({
      reason: rejectReason,
      reason_text:
        rejectReason === 'other' ? rejectText.trim() : rejectText.trim() || undefined,
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="w-full max-w-2xl"
      aria-label={t('topup_review.modal.ariaLabel')}
      data-testid="topup-proof-review-modal"
    >
      <DialogHeader>
        <DialogTitle>{t('topup_review.modal.title')}</DialogTitle>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm text-pw-text-muted">
          <div className="flex items-center gap-3">
            <span className="font-medium text-pw-text">{row.merchant_name}</span>
            <span className="font-mono text-xs" title={row.merchant_id}>
              {row.merchant_id.slice(-8)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
                slaClass,
              )}
              data-testid="topup-sla-badge"
              data-sla={row.sla_badge}
              title={t('topup_review.modal.ageMinutes', { count: row.age_minutes })}
            >
              {t('topup_review.modal.sla', { age: formatAgeMinutes(row.age_minutes) })}
            </span>
            <Money usdCents={row.amount_cents} />
          </div>
        </div>
      </DialogHeader>

      {/* Sections — each section is opt-in based on whichever fields the
          BE populated. The modal degrades gracefully when proof is null. */}
      <div className="mt-4 max-h-[60vh] overflow-y-auto space-y-4">
        {/* Section 1 — IMAP raw + parsed */}
        <Section
          title={t('topup_review.modal.section1Title')}
          subtitle={t('topup_review.modal.section1Subtitle')}
        >
          <div className="space-y-2">
            <div className="rounded-md border border-pw-border bg-pw-surface p-2 text-xs">
              <div className="mb-1 font-semibold text-pw-text-muted uppercase tracking-wide">
                {t('topup_review.modal.parsed')}
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                <dt className="text-pw-text-muted">{t('topup_review.modal.sender')}</dt>
                <dd className="font-mono break-all">
                  {proof?.imap_parsed?.sender ?? '—'}
                </dd>
                <dt className="text-pw-text-muted">{t('topup_review.modal.amount')}</dt>
                <dd className="font-mono">
                  {proof?.imap_parsed?.amount ?? '—'}
                </dd>
                <dt className="text-pw-text-muted">{t('topup_review.modal.memo')}</dt>
                <dd className="font-mono break-all">
                  {proof?.imap_parsed?.memo ?? '—'}
                </dd>
                <dt className="text-pw-text-muted">{t('topup_review.modal.timestamp')}</dt>
                <dd className="font-mono">
                  {proof?.imap_parsed?.timestamp ?? '—'}
                </dd>
              </dl>
            </div>
            <details className="rounded-md border border-pw-border bg-pw-surface">
              <summary className="cursor-pointer px-2 py-1 text-xs font-semibold text-pw-text-muted">
                {t('topup_review.modal.rawImap')}
              </summary>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all p-2 font-mono text-[11px] text-pw-text">
                {proof?.imap_raw ?? row.imap_raw_preview ?? t('topup_review.modal.noImap')}
              </pre>
            </details>
          </div>
        </Section>

        {/* Section 2 — Merchant memo */}
        <Section
          title={t('topup_review.modal.section2Title')}
          subtitle={t('topup_review.modal.section2Subtitle')}
        >
          <p className="whitespace-pre-wrap rounded-md border border-pw-border bg-pw-surface p-2 text-sm text-pw-text">
            {row.memo?.trim() ? row.memo : <em className="text-pw-text-muted">{t('topup_review.modal.noMemo')}</em>}
          </p>
        </Section>

        {/* Section 3 — Screenshot / bill upload (≤5MB enforced BE-side) */}
        <Section
          title={t('topup_review.modal.section3Title')}
          subtitle={t('topup_review.modal.section3Subtitle')}
        >
          {proofUrl ? (
            <div className="space-y-2">
              {/* When the bytes fail to load (deleted file, storage outage,
                  non-image mime such as a PDF receipt) we swap the broken
                  <img> for an explicit open-in-new-tab link so the reviewer
                  is never left staring at an empty box — issue #2815. */}
              {proofLoadFailed ? (
                <p className="text-sm text-pw-text-muted">
                  {t('topup_review.modal.screenshotLoadFailed')}
                </p>
              ) : (
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block max-w-full"
                  data-testid="topup-proof-image-link"
                >
                  <img
                    src={proofUrl}
                    alt={t('topup_review.modal.screenshotAlt')}
                    className="max-h-56 w-auto rounded-md border border-pw-border object-contain"
                    data-testid="topup-proof-image"
                    onError={() => setProofLoadFailed(true)}
                  />
                </a>
              )}
              <a
                href={proofUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-xs text-pw-primary-strong hover:underline"
                data-testid="topup-proof-open-link"
              >
                {t('topup_review.modal.screenshotOpen')}
              </a>
            </div>
          ) : (
            <p className="text-sm italic text-pw-text-muted">
              {t('topup_review.modal.noScreenshot')}
            </p>
          )}
        </Section>

        {/* Section 4 — Transaction history + wallet balance + flags */}
        <Section
          title={t('topup_review.modal.section4Title')}
          subtitle={t('topup_review.modal.section4Subtitle')}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md border border-pw-border bg-pw-surface p-2 text-sm">
              <span className="text-pw-text-muted">{t('topup_review.modal.walletBalance')}</span>
              {proof?.wallet_balance_cents != null ? (
                <Money usdCents={proof.wallet_balance_cents} />
              ) : (
                <span className="text-pw-text-muted">—</span>
              )}
            </div>
            {proof?.suspicious_flags && proof.suspicious_flags.length > 0 ? (
              <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/40 p-2 text-xs text-rose-800 dark:text-rose-300">
                <div className="font-semibold uppercase tracking-wide">
                  {t('topup_review.modal.suspiciousFlags')}
                </div>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {proof.suspicious_flags.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {proof?.recent_transactions && proof.recent_transactions.length > 0 ? (
              <div className="rounded-md border border-pw-border bg-pw-surface">
                <div className="border-b border-pw-border px-2 py-1 text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('topup_review.modal.recentTransactions')}
                </div>
                <ul className="divide-y divide-pw-border text-xs">
                  {proof.recent_transactions.map((tx) => (
                    <li key={tx.id} className="flex items-center justify-between px-2 py-1.5">
                      <span className="font-mono text-pw-text-muted">{tx.type}</span>
                      <Money usdCents={tx.amount_cents} />
                      <span className="text-pw-text-muted">
                        {new Date(tx.created_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs italic text-pw-text-muted">
                {t('topup_review.modal.noTransactions')}
              </p>
            )}
          </div>
        </Section>

        {/* Inline approve note + reject form */}
        <div className="rounded-md border border-pw-border bg-pw-bg p-3">
          <label className="block text-xs font-medium uppercase tracking-wide text-pw-text-muted">
            {t('topup_review.modal.approveNotes')}
          </label>
          <Input
            className="mt-1"
            placeholder={t('topup_review.modal.approveNotesPlaceholder')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="rounded-md border border-pw-border bg-pw-bg p-3">
          <label className="block text-xs font-medium uppercase tracking-wide text-pw-text-muted">
            {t('topup_review.modal.rejectThis')}
          </label>
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
            <Select
              value={rejectReason || undefined}
              onValueChange={(value) =>
                setRejectReason(value as TopupRejectReason)
              }
              disabled={submitting}
            >
              <SelectTrigger
                data-testid="topup-reject-reason-select"
                aria-label={t('topup_review.modal.rejectReasonAria')}
              >
                <SelectValue
                  placeholder={t('topup_review.modal.selectReason')}
                />
              </SelectTrigger>
              <SelectContent>
                {TOPUP_REJECT_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {t(r.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {rejectReason === 'other' ? (
            <Input
              className="mt-2"
              data-testid="topup-reject-reason-other-input"
              placeholder={t('topup_review.modal.freeTextPlaceholder')}
              value={rejectText}
              onChange={(e) => setRejectText(e.target.value)}
              disabled={submitting}
              aria-invalid={!!rejectFormError}
            />
          ) : null}
          {rejectFormError ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">{rejectFormError}</p>
          ) : null}
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          {t('topup_review.modal.cancel')}
        </Button>
        <Button
          variant="destructive"
          data-testid="topup-reject-btn"
          onClick={() => void handleReject()}
          disabled={submitting}
        >
          {t('topup_review.modal.reject')}
        </Button>
        <Button
          variant="default"
          data-testid="topup-modal-approve-btn"
          onClick={() => void handleApprove()}
          disabled={submitting}
        >
          {t('topup_review.modal.approve')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <section className="rounded-md border border-pw-border bg-pw-bg p-3">
      <header className="mb-2">
        <h3 className="text-sm font-semibold text-pw-text">{title}</h3>
        {subtitle ? (
          <p className="text-xs text-pw-text-muted">{subtitle}</p>
        ) : null}
      </header>
      <div>{children}</div>
    </section>
  );
}

/**
 * Map the BE-computed `sla_badge` to Tailwind classes.
 * `green` < 2h | `yellow` 2-4h | `red` > 4h. Stays in sync with the row
 * pill in the parent queue page so the colour signal is consistent.
 */
function slaBadgeClass(b: 'green' | 'yellow' | 'red'): string {
  switch (b) {
    case 'green':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    case 'yellow':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    case 'red':
      return 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800';
  }
}

function formatAgeMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default TopupProofReviewModal;
